import { useState, useEffect, useRef } from "react";
import { MAP_STYLES, MAP_STYLE_ORDER } from "./mapStyles";
import { Layers, Check } from "lucide-react";

/**
 * Compact dropdown to switch between map tile styles.
 * Single circular button that expands into a small floating menu on tap.
 *
 * Props:
 *  - value       : current style id
 *  - onChange(id): callback when user selects a style
 *  - className   : extra classes for the trigger button
 */
export default function MapStyleSwitcher({ value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const current = MAP_STYLES[value] || MAP_STYLES[MAP_STYLE_ORDER[0]];

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid="map-style-switcher">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="تبديل نمط الخريطة"
        aria-expanded={open}
        className="size-11 rounded-full bg-slate-900/90 backdrop-blur-xl border border-white/15 text-white flex items-center justify-center shadow-xl hover:bg-slate-800 active:scale-95 transition"
        data-testid="map-style-trigger"
      >
        <Layers className="size-5" />
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          className="absolute top-full mt-2 end-0 min-w-[180px] rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/15 shadow-2xl overflow-hidden py-1"
          role="menu"
          data-testid="map-style-menu"
        >
          {MAP_STYLE_ORDER.map((id) => {
            const s = MAP_STYLES[id];
            const active = value === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => { onChange(id); setOpen(false); }}
                role="menuitemradio"
                aria-checked={active}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-start transition ${
                  active ? "bg-primary/15 text-white" : "text-white/85 hover:bg-white/10"
                }`}
                data-testid={`map-style-${id}`}
              >
                <span className="text-lg leading-none">{s.emoji}</span>
                <span className="flex-1 text-sm font-bold">{s.label}</span>
                {active && <Check className="size-4 text-primary" />}
              </button>
            );
          })}
        </div>
      )}

      {/* Tiny label preview under the button (which style is active) */}
      <div className="absolute top-full mt-1 end-0 text-[10px] font-bold text-white/70 bg-slate-900/70 rounded-full px-2 py-0.5 backdrop-blur border border-white/10 whitespace-nowrap pointer-events-none">
        {current.emoji} {current.label}
      </div>
    </div>
  );
}
